/**
 * Utilitários para CPF e Telefone — validação e formatação
 * @internal true
 * @owner frontend
 * Sprint 1 — Correção do Wizard de Matrícula
 */

/**
 * Remove todos os caracteres não numéricos de uma string.
 */
export function stripMask(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Valida CPF brasileiro usando os dígitos verificadores (algoritmo padrão da Receita Federal).
 * Aceita CPF com ou sem máscara.
 * Retorna false para CPFs com todos os dígitos iguais (ex: 111.111.111-11).
 */
export function validateCPF(cpf: string): boolean {
  const digits = stripMask(cpf);

  if (digits.length !== 11) return false;

  // Rejeita CPFs com todos os dígitos iguais
  if (/^(\d)\1{10}$/.test(digits)) return false;

  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(digits[i]) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[9])) return false;

  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(digits[i]) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10) remainder = 0;
  if (remainder !== parseInt(digits[10])) return false;

  return true;
}

/**
 * Formata uma string numérica como CPF: 000.000.000-00
 * Aplica a máscara progressivamente conforme o usuário digita.
 */
export function formatCPF(value: string): string {
  const digits = stripMask(value).slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Formata uma string numérica como telefone brasileiro: (00) 00000-0000
 * Suporta fixo (8 dígitos) e celular (9 dígitos).
 */
export function formatPhone(value: string): string {
  const digits = stripMask(value).slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Formata uma string numérica como CEP brasileiro: 00000-000
 * Aplica a máscara progressivamente conforme o usuário digita.
 */
export function formatCEP(value: string): string {
  const digits = stripMask(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}
